/**
 * useHeadToHeadData Hook
 * 
 * Phase 4: Three-Mode System + Correct Field Mapping
 * 
 * ROSTER STRUCTURE (from /entry endpoint):
 * {
 *   player: { id, name, team, pos },
 *   prop: { id, stat, line, ... },
 *   side: "over" | "under",
 *   units: 20,
 *   resolution: { status: "HIT"|"MISS"|"PENDING", payout, actualValue, resolvedAt }
 * }
 * 
 * THREE MODES (Architect LOCKED):
 * 1. LIVE - /live-state 200 + status 'ok'/'stale' → full features, polling
 * 2. PENDING - /live-state 200 + status 'awaiting_first_refresh' → roster render, KEEP polling
 * 3. HISTORICAL - /live-state 404 ONLY → roster render, STOP polling
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// =============================================================================
// API Configuration
// =============================================================================

const API_BASE = import.meta.env.VITE_API_BASE || 'https://north-star-mvp.onrender.com';

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_POLLING_INTERVAL = 30000;

const MODE = {
  LIVE: 'live',
  PENDING: 'pending',
  HISTORICAL: 'historical',
};

// =============================================================================
// Helper Functions
// =============================================================================

export const calculateIsHunting = (opponent, you) => {
  if (!opponent || !you) return false;
  const yourScore = you.score ?? 0;
  const oppScore = opponent.score ?? 0;
  const gap = yourScore - oppScore;
  const impactRemaining = opponent.impact_remaining_units ?? opponent.units?.impact_remaining_units ?? 0;
  
  if (!impactRemaining) return false;
  return gap > 0 && impactRemaining >= gap;
};

export const getTracking = (pick) => {
  if (pick.status !== 'live') return null;
  
  const { current, line, direction } = pick;
  if (current === null || current === undefined) return null;
  
  if (direction === 'Above' || direction === 'ABOVE' || direction === 'OVER') {
    if (current >= line) return { text: 'HIT', color: 'emerald' };
    return { text: `need ${Math.ceil(line - current)}`, color: 'amber' };
  } else {
    if (current >= line) return { text: 'MISS', color: 'red' };
    return { text: `${Math.floor(line - current)} left`, color: 'emerald' };
  }
};

export const getSection = (pick) => {
  // Check resolution status first (from actual API)
  const resStatus = pick.resolutionStatus?.toUpperCase();
  if (resStatus === 'HIT' || resStatus === 'MISS' || resStatus === 'PUSH') {
    return 'finished';
  }
  
  // Then check event_status
  switch (pick.event_status) {
    case 'LIVE': return 'field';
    case 'UPCOMING': return 'locker';
    case 'FINAL': return 'finished';
    default:
      if (pick.status === 'live') return 'field';
      if (pick.status === 'upcoming') return 'locker';
      return 'finished';
  }
};

const normalizeDirection = (side) => {
  if (!side) return '';
  const s = side.toUpperCase();
  if (s === 'OVER' || s === 'ABOVE') return 'Above';
  if (s === 'UNDER' || s === 'BELOW') return 'Below';
  return side;
};

const normalizeResolutionStatus = (resolution) => {
  if (!resolution || !resolution.status) return 'upcoming';
  const s = resolution.status.toUpperCase();
  if (s === 'HIT') return 'hit';
  if (s === 'MISS') return 'miss';
  if (s === 'PUSH') return 'push';
  if (s === 'PENDING' || s === 'IN_PLAY') return 'live';
  return 'upcoming';
};

/**
 * Extract allocation data from nested roster structure
 * 
 * Input: { player: {...}, prop: {...}, side, units, resolution: {...} }
 * Output: Normalized allocation object
 */
const extractAllocation = (rosterItem) => {
  const player = rosterItem.player || {};
  const prop = rosterItem.prop || {};
  const resolution = rosterItem.resolution || {};
  
  return {
    id: rosterItem.slot || prop.id,
    name: player.name || 'Unknown',
    team: player.team || '',
    pos: player.pos || '',
    stat: prop.stat || '',
    line: prop.line || 0,
    direction: normalizeDirection(rosterItem.side),
    current: resolution.actualValue ?? null,
    units: rosterItem.units || 0,
    status: normalizeResolutionStatus(resolution),
    resolutionStatus: resolution.status || null,
    payout: resolution.payout || 0,
    // Determine event_status based on resolution (FIX A: only HIT/MISS/PUSH = FINAL)
    event_status: (() => {
      const rs = (resolution.status || '').toUpperCase();
      const isResolved = (rs === 'HIT' || rs === 'MISS' || rs === 'PUSH');
      const hasActual = resolution.actualValue !== null && resolution.actualValue !== undefined;
      return isResolved ? 'FINAL' : (hasActual ? 'LIVE' : 'UPCOMING');
    })(),
  };
};

/**
 * Group player allocations by player name
 */
export const groupPlayersByName = (yourRoster = [], opponentRoster = []) => {
  const playerMap = new Map();
  
  // Process your roster items
  yourRoster.forEach(rosterItem => {
    const alloc = extractAllocation(rosterItem);
    const name = alloc.name;
    
    if (!playerMap.has(name)) {
      playerMap.set(name, {
        name,
        team: alloc.team,
        pos: alloc.pos,
        yourTargets: [],
        opponentTargets: [],
        event_status: alloc.event_status,
      });
    }
    
    const player = playerMap.get(name);
    player.yourTargets.push({
      id: alloc.id,
      stat: alloc.stat,
      line: alloc.line,
      direction: alloc.direction,
      current: alloc.current,
      status: alloc.status,
      units: alloc.units,
      event_status: alloc.event_status,
      payout: alloc.payout,
    });
    
    // Update to LIVE if any target is live
    if (alloc.event_status === 'LIVE') {
      player.event_status = 'LIVE';
    }
  });
  
  // Process opponent roster items
  opponentRoster.forEach(rosterItem => {
    const alloc = extractAllocation(rosterItem);
    const name = alloc.name;
    
    if (!playerMap.has(name)) {
      playerMap.set(name, {
        name,
        team: alloc.team,
        pos: alloc.pos,
        yourTargets: [],
        opponentTargets: [],
        event_status: alloc.event_status,
      });
    }
    
    const player = playerMap.get(name);
    player.opponentTargets.push({
      id: alloc.id,
      stat: alloc.stat,
      line: alloc.line,
      direction: alloc.direction,
      current: alloc.current,
      status: alloc.status,
      units: alloc.units,
      event_status: alloc.event_status,
      payout: alloc.payout,
    });
    
    if (alloc.event_status === 'LIVE') {
      player.event_status = 'LIVE';
    }
  });
  
  const players = Array.from(playerMap.values());
  const sectionOrder = { 'LIVE': 0, 'UPCOMING': 1, 'FINAL': 2 };
  players.sort((a, b) => {
    const aOrder = sectionOrder[a.event_status] ?? 2;
    const bOrder = sectionOrder[b.event_status] ?? 2;
    return aOrder - bOrder;
  });
  
  return players;
};

export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMin = Math.floor((now - then) / 60000);
  
  if (diffMin < 1) return 'just now';
  if (diffMin === 1) return '1 min ago';
  if (diffMin < 60) return `${diffMin} min ago`;
  
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours === 1) return '1 hour ago';
  return `${diffHours} hours ago`;
};

// =============================================================================
// Main Hook
// =============================================================================

export function useHeadToHeadData(poolId, opponentUserId, currentUserId = 'larry', options = {}) {
  const { 
    enablePolling = true, 
    pollingInterval = DEFAULT_POLLING_INTERVAL 
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  const lastScoresRef = useRef({});
  const lastSnapshotSeqRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const modeRef = useRef(MODE.PENDING);

  const calculateDeltas = useCallback((yourScore, opponentScore, snapshotSeq) => {
    const deltas = { you: 0, opponent: 0 };
    
    if (lastScoresRef.current.you !== undefined) {
      deltas.you = yourScore - lastScoresRef.current.you;
    }
    if (lastScoresRef.current.opponent !== undefined) {
      deltas.opponent = opponentScore - lastScoresRef.current.opponent;
    }
    
    if (snapshotSeq !== lastSnapshotSeqRef.current) {
      lastScoresRef.current = { you: yourScore, opponent: opponentScore };
      lastSnapshotSeqRef.current = snapshotSeq;
    }
    
    return deltas;
  }, []);

  const fetchRosters = useCallback(async (abortController) => {
    // Fetch YOUR roster
    const yourRosterRes = await fetch(`${API_BASE}/api/v1/pools/${poolId}/entry`, {
      headers: { 'X-User-ID': currentUserId },
      signal: abortController.signal,
    });
    
    if (abortController.signal.aborted) return null;
    
    let yourRoster = [];
    let yourScore = 0;
    let yourTotalUnits = 0;
    if (yourRosterRes.ok) {
      const yourData = await yourRosterRes.json();
      yourRoster = yourData.entry?.roster || yourData.roster || [];
      yourScore = yourData.entry?.score ?? yourData.entry?.current_balance ?? yourData.score ?? 0;
      yourTotalUnits = yourData.entry?.totalUnits ?? 100;
    }
    
    // Fetch OPPONENT roster
    const opponentRosterRes = await fetch(`${API_BASE}/api/v1/pools/${poolId}/entry`, {
      headers: { 'X-User-ID': opponentUserId },
      signal: abortController.signal,
    });
    
    if (abortController.signal.aborted) return null;
    
    let opponentRoster = [];
    let opponentScore = 0;
    let opponentTotalUnits = 0;
    if (opponentRosterRes.ok) {
      const oppData = await opponentRosterRes.json();
      opponentRoster = oppData.entry?.roster || oppData.roster || [];
      opponentScore = oppData.entry?.score ?? oppData.entry?.current_balance ?? oppData.score ?? 0;
      opponentTotalUnits = oppData.entry?.totalUnits ?? 100;
    }
    
    return { 
      yourRoster, yourScore, yourTotalUnits,
      opponentRoster, opponentScore, opponentTotalUnits 
    };
  }, [poolId, currentUserId, opponentUserId]);

  const fetchData = useCallback(async (isPolling = false) => {
    if (!poolId || !opponentUserId) {
      setError(new Error('Missing poolId or opponentUserId'));
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);
      
      // Only skip polling in HISTORICAL mode (404)
      if (modeRef.current === MODE.HISTORICAL && isPolling) {
        console.log('[useHeadToHeadData] Skipping poll (HISTORICAL mode)');
        return;
      }
      
      // 1. Try to fetch live-state
      let liveStateData = null;
      let liveStateStatus = null;
      
      try {
        const liveStateRes = await fetch(
          `${API_BASE}/api/v1/pools/${poolId}/live-state?limit=100`,
          {
            headers: { 'X-User-ID': currentUserId },
            signal: abortController.signal,
          }
        );
        
        if (abortController.signal.aborted) return;
        
        if (liveStateRes.ok) {
          liveStateData = await liveStateRes.json();
          liveStateStatus = 200;
        } else if (liveStateRes.status === 404) {
          liveStateStatus = 404;
          console.log('[useHeadToHeadData] live-state 404 → HISTORICAL mode');
        }
      } catch (liveStateErr) {
        if (liveStateErr.name === 'AbortError') return;
        console.log('[useHeadToHeadData] live-state error:', liveStateErr.message);
      }
      
      // 2. Determine mode
      if (liveStateStatus === 404) {
        modeRef.current = MODE.HISTORICAL;
      } else if (liveStateData) {
        const status = liveStateData.status;
        if (status === 'ok' || status === 'stale') {
          modeRef.current = MODE.LIVE;
        } else {
          modeRef.current = MODE.PENDING;
        }
      }
      
      // 3. Fetch rosters (always needed)
      const rosters = await fetchRosters(abortController);
      if (!rosters || abortController.signal.aborted) return;
      
      const { 
        yourRoster, yourScore, yourTotalUnits,
        opponentRoster, opponentScore, opponentTotalUnits 
      } = rosters;
      
      // 4. Group players and build sections
      const groupedPlayers = groupPlayersByName(yourRoster, opponentRoster);
      const onField = groupedPlayers.filter(p => getSection({ event_status: p.event_status }) === 'field');
      const locker = groupedPlayers.filter(p => getSection({ event_status: p.event_status }) === 'locker');
      const finished = groupedPlayers.filter(p => getSection({ event_status: p.event_status }) === 'finished');
      
      // 5. Extract live-state data if available
      const lsYou = liveStateData?.you || {};
      const competitors = liveStateData?.competitors || [];
      const lsOpponent = competitors.find(c => c.user_id === opponentUserId) || {};
      const snapshot = liveStateData?.snapshot || {};
      
      // Use live-state scores if available, otherwise roster scores
      const finalYourScore = lsYou.score ?? yourScore;
      const finalOpponentScore = lsOpponent.score ?? opponentScore;
      
      // Calculate deltas only in LIVE mode
      const deltas = modeRef.current === MODE.LIVE 
        ? calculateDeltas(finalYourScore, finalOpponentScore, snapshot.snapshot_seq)
        : { you: 0, opponent: 0 };
      
      // Calculate hunting only in LIVE mode
      const isHunting = modeRef.current === MODE.LIVE 
        ? calculateIsHunting(lsOpponent, lsYou)
        : false;
      
      // Calculate units remaining (total - used based on resolution)
      const yourUnitsUsed = yourRoster.reduce((sum, r) => sum + (r.units || 0), 0);
      const opponentUnitsUsed = opponentRoster.reduce((sum, r) => sum + (r.units || 0), 0);
      
      // 6. Build final data
      const newData = {
        you: {
          user_id: lsYou.user_id || currentUserId,
          name: lsYou.display_name || currentUserId,
          score: finalYourScore,
          delta: deltas.you,
          units_remaining: yourTotalUnits - yourUnitsUsed,
          impact_remaining_units: lsYou.units?.impact_remaining_units ?? 0,
          rank: lsYou.rank || 0,
        },
        opponent: {
          user_id: lsOpponent.user_id || opponentUserId,
          name: lsOpponent.display_name || opponentUserId,
          score: finalOpponentScore,
          delta: deltas.opponent,
          units_remaining: opponentTotalUnits - opponentUnitsUsed,
          impact_remaining_units: lsOpponent.units?.impact_remaining_units ?? 0,
          rank: lsOpponent.rank || 0,
          isHunting,
        },
        snapshot: {
          status: liveStateData?.status || 'historical',
          timestamp: snapshot.snapshot_time || new Date().toISOString(),
          snapshot_seq: snapshot.snapshot_seq || null,
        },
        mode: modeRef.current,
        isLive: modeRef.current === MODE.LIVE,
        isAwaitingFirstRefresh: liveStateData?.status === 'awaiting_first_refresh',
        isHistoricalMode: modeRef.current === MODE.HISTORICAL,
        isPendingMode: modeRef.current === MODE.PENDING,
        players: groupedPlayers,
        sections: { onField, locker, finished },
      };
      
      if (isMountedRef.current) {
        setData(newData);
        setLastFetchTime(new Date().toISOString());
      }
      
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[useHeadToHeadData] Error:', err);
      if (isMountedRef.current && !isPolling) {
        setError(err);
      }
    } finally {
      if (isMountedRef.current && !isPolling) {
        setLoading(false);
      }
    }
  }, [poolId, opponentUserId, currentUserId, calculateDeltas, fetchRosters]);

  const refetch = useCallback(() => {
    modeRef.current = MODE.PENDING;
    fetchData(false);
  }, [fetchData]);

  // Reset state on param change to avoid stale UI flash
  useEffect(() => {
    setData(null);
    setError(null);
    modeRef.current = MODE.PENDING;
    lastScoresRef.current = {};
    lastSnapshotSeqRef.current = null;
  }, [poolId, opponentUserId]);

  // Initial fetch - depends on URL params directly
  useEffect(() => {
    if (!poolId || !opponentUserId) return;
    
    isMountedRef.current = true;
    fetchData(false);
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [poolId, opponentUserId, fetchData]);

  // Polling - only stops in HISTORICAL mode
  useEffect(() => {
    if (!enablePolling || !poolId || !opponentUserId) return;
    
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && modeRef.current !== MODE.HISTORICAL) {
        fetchData(true);
      }
    }, pollingInterval);
    
    return () => clearInterval(intervalId);
  }, [enablePolling, pollingInterval, fetchData, poolId, opponentUserId]);

  // Tab visibility
  useEffect(() => {
    if (!enablePolling) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && modeRef.current !== MODE.HISTORICAL) {
        fetchData(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enablePolling, fetchData]);

  return { 
    data, 
    loading, 
    error, 
    refetch,
    lastFetchTime,
    getTimeAgo: () => formatTimeAgo(lastFetchTime),
  };
}

export default useHeadToHeadData;
